import { createSelector } from '@reduxjs/toolkit';

import type { PropsCoreState } from '~/store/interfaces';

import type { Contract, ContractId, ContractInstallment } from './interfaces';

const DAY = 24 * 60 * 60 * 1000;

export const isContractSettled = (c?: Contract): boolean =>
  !!c && (c.status === 'completed' || c.amountRemaining <= 0);

export const selectContracts = ({ contracts }: PropsCoreState) => contracts;
export const selectContractsById = ({ contracts }: PropsCoreState) => contracts.byId;
export const selectContractsAllIds = ({ contracts }: PropsCoreState) => contracts.allIds;
export const selectInstallmentsByContractId = ({ contracts }: PropsCoreState) =>
  contracts.installmentsByContractId;
export const selectSnoozedAlerts = ({ contracts }: PropsCoreState) =>
  contracts.snoozedAlerts;
export const selectRecentUploads = ({ contracts }: PropsCoreState) =>
  contracts.recentUploads ?? [];
export const selectContractsCount = ({ contracts }: PropsCoreState) =>
  contracts.allIds.length;

export const selectContractsList = createSelector(
  [selectContractsById, selectContractsAllIds],
  (byId, allIds): Contract[] => allIds.map((id) => byId[id]).filter(Boolean),
);

export const selectContractById = (id: ContractId) =>
  createSelector([selectContractsById], (byId) => byId[id]);

export const selectInstallmentsForContract = (contractId: ContractId) =>
  createSelector(
    [selectInstallmentsByContractId],
    (map): ContractInstallment[] => map[contractId] ?? [],
  );

export const selectAllInstallments = createSelector(
  [selectInstallmentsByContractId],
  (map): ContractInstallment[] => Object.values(map).flat() as ContractInstallment[],
);

export const selectTotalDebt = createSelector([selectContractsList], (list) =>
  list.reduce((sum, c) => sum + c.amountRemaining, 0),
);

export const selectTotalPaidThisYear = createSelector(
  [selectAllInstallments],
  (list) => {
    const startOfYear = new Date();
    startOfYear.setMonth(0, 1);
    startOfYear.setHours(0, 0, 0, 0);
    return list
      .filter(
        (i) =>
          i.status === 'paid' &&
          i.paidAt &&
          new Date(i.paidAt).getTime() >= startOfYear.getTime(),
      )
      .reduce((s, i) => s + i.amount, 0);
  },
);

export const selectDebtChangeVsPreviousMonth = createSelector(
  [selectContractsList, selectAllInstallments],
  (contracts, installments) => {
    const now = new Date();
    const startPrev = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
    const startCur = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

    const paidLastMonth = installments
      .filter((i) => i.status === 'paid' && i.paidAt)
      .filter((i) => {
        const t = new Date(i.paidAt as string).getTime();
        return t >= startPrev && t < startCur;
      })
      .reduce((s, i) => s + i.amount, 0);

    const addedLastMonth = contracts
      .filter((c) => {
        const t = new Date(c.createdAt).getTime();
        return t >= startPrev && t < startCur;
      })
      .reduce((s, c) => s + c.amountTotal, 0);

    return addedLastMonth - paidLastMonth;
  },
);

export const selectNextInstallment = createSelector(
  [selectAllInstallments],
  (list): ContractInstallment | undefined => {
    const upcoming = list
      .filter((i) => i.status === 'next' || i.status === 'overdue')
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
    return upcoming[0];
  },
);

export interface DebtBucket {
  key: 'lt90' | 'lt180' | 'lt360' | 'lt720' | 'gt720';
  label: string;
  amount: number;
  contractsCount: number;
  riskLevel: 'low' | 'medium' | 'high';
}

const BUCKET_DEFS: { key: DebtBucket['key']; label: string; max: number }[] = [
  { key: 'lt90', label: 'Até 90 dias', max: 90 },
  { key: 'lt180', label: 'Até 180 dias', max: 180 },
  { key: 'lt360', label: 'Até 360 dias', max: 360 },
  { key: 'lt720', label: 'Até 720 dias', max: 720 },
  { key: 'gt720', label: 'Acima de 720 dias', max: Infinity },
];

const riskForBucket = (key: DebtBucket['key']): DebtBucket['riskLevel'] => {
  if (key === 'lt90') return 'high';
  if (key === 'lt180' || key === 'lt360') return 'medium';
  return 'low';
};

export const selectDebtByBucket = createSelector(
  [selectAllInstallments],
  (installments): DebtBucket[] => {
    const today = Date.now();
    const buckets: Record<DebtBucket['key'], DebtBucket> = {
      lt90: { key: 'lt90', label: 'Até 90 dias', amount: 0, contractsCount: 0, riskLevel: 'high' },
      lt180: { key: 'lt180', label: 'Até 180 dias', amount: 0, contractsCount: 0, riskLevel: 'medium' },
      lt360: { key: 'lt360', label: 'Até 360 dias', amount: 0, contractsCount: 0, riskLevel: 'medium' },
      lt720: { key: 'lt720', label: 'Até 720 dias', amount: 0, contractsCount: 0, riskLevel: 'low' },
      gt720: { key: 'gt720', label: 'Acima de 720 dias', amount: 0, contractsCount: 0, riskLevel: 'low' },
    };

    const contractsInBucket: Record<DebtBucket['key'], Set<ContractId>> = {
      lt90: new Set(), lt180: new Set(), lt360: new Set(), lt720: new Set(), gt720: new Set(),
    };

    installments
      .filter((i) => i.status !== 'paid')
      .forEach((i) => {
        const days = (new Date(i.dueDate).getTime() - today) / DAY;
        const def = BUCKET_DEFS.find((b) => days < b.max);
        if (!def) return;
        const { key } = def;
        buckets[key].amount += i.amount;
        contractsInBucket[key].add(i.contractId);
      });

    BUCKET_DEFS.forEach(({ key }) => {
      buckets[key].contractsCount = contractsInBucket[key].size;
      buckets[key].riskLevel = riskForBucket(key);
    });

    return BUCKET_DEFS.map(({ key }) => buckets[key]);
  },
);

export const selectOverdueInstallments = createSelector(
  [selectAllInstallments],
  (list) =>
    list
      .filter((i) => i.status === 'overdue')
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()),
);

const inWindow = (i: ContractInstallment, fromDays: number, toDays: number) => {
  const now = Date.now();
  const due = new Date(i.dueDate).getTime();
  return due >= now + fromDays * DAY && due <= now + toDays * DAY;
};

export type AlertBucketTone = 'critical' | 'warning' | 'neutral';
export type AlertStatus = 'critical' | 'warning' | 'success';

export interface AlertInstallmentItem {
  installment: ContractInstallment;
  contract?: Contract;
  tone: AlertBucketTone;
}

export interface AlertSection {
  key: string;
  title: string;
  items: AlertInstallmentItem[];
}

export interface AlertOverview {
  sections: AlertSection[];
  status: AlertStatus;
}

type AlertSectionKey = 'now' | 'd7' | 'd60' | 'd90' | 'y1' | 'y1plus';

const ALERT_SECTION_DEFS: { key: AlertSectionKey; title: string }[] = [
  { key: 'now', title: 'Hoje & Atrasado' },
  { key: 'd7', title: 'Próximos 7 dias' },
  { key: 'd60', title: 'Próximos 60 dias' },
  { key: 'd90', title: 'Próximos 90 dias' },
  { key: 'y1', title: 'Até 1 ano' },
  { key: 'y1plus', title: 'Mais de 1 ano' },
];

const calendarDayIndex = (d: Date) =>
  Math.floor(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / DAY,
  );

const classifyInstallment = (diff: number): { section: AlertSectionKey; tone: AlertBucketTone } => {
  if (diff < 0) return { section: 'now', tone: 'critical' };
  if (diff === 0) return { section: 'now', tone: 'warning' };
  if (diff <= 7) return { section: 'd7', tone: 'warning' };
  if (diff <= 60) return { section: 'd60', tone: 'neutral' };
  if (diff <= 90) return { section: 'd90', tone: 'neutral' };
  if (diff <= 365) return { section: 'y1', tone: 'neutral' };
  return { section: 'y1plus', tone: 'neutral' };
};

export const selectAlertOverview = createSelector(
  [selectAllInstallments, selectContractsById],
  (installments, contractsById): AlertOverview => {
    const now = new Date();
    const todayIndex = Math.floor(
      Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / DAY,
    );

    const groups: Record<AlertSectionKey, AlertInstallmentItem[]> = {
      now: [], d7: [], d60: [], d90: [], y1: [], y1plus: [],
    };

    let within30 = 0;
    let within60 = 0;

    installments
      .filter((i) => i.status !== 'paid')
      .forEach((i) => {
        const diff = calendarDayIndex(new Date(i.dueDate)) - todayIndex;
        const { section, tone } = classifyInstallment(diff);
        groups[section].push({
          installment: i,
          contract: contractsById[i.contractId],
          tone,
        });
        if (diff <= 30) within30 += 1;
        if (diff <= 60) within60 += 1;
      });

    const byDueDateAsc = (a: AlertInstallmentItem, b: AlertInstallmentItem) =>
      new Date(a.installment.dueDate).getTime() -
      new Date(b.installment.dueDate).getTime();

    const sections: AlertSection[] = ALERT_SECTION_DEFS.map((def) => ({
      key: def.key,
      title: def.title,
      items: [...groups[def.key]].sort(byDueDateAsc),
    })).filter((s) => s.items.length > 0);

    let status: AlertStatus = 'success';
    if (within30 > 0) status = 'critical';
    else if (within60 > 0) status = 'warning';

    return { sections, status };
  },
);

export const selectContractsRecent = createSelector(
  [selectContractsList, selectInstallmentsByContractId],
  (list, installmentsByContract) => {
    const nextDueTime = (id: ContractId): number => {
      const next = [...(installmentsByContract[id] ?? [])]
        .filter((i) => i.status !== 'paid')
        .sort(
          (a, b) =>
            new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
        )[0];
      return next ? new Date(next.dueDate).getTime() : Infinity;
    };
    return list
      .filter((c) => !isContractSettled(c))
      .sort((a, b) => nextDueTime(a.id) - nextDueTime(b.id))
      .slice(0, 3);
  },
);

export const selectNextUnpaidByContract = createSelector(
  [selectInstallmentsByContractId],
  (map): Record<ContractId, ContractInstallment | undefined> => {
    const result: Record<ContractId, ContractInstallment | undefined> = {};
    Object.entries(map).forEach(([contractId, list]) => {
      result[contractId] = [...(list ?? [])]
        .filter((i) => i.status !== 'paid')
        .sort(
          (a, b) =>
            new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
        )[0];
    });
    return result;
  },
);

export const selectAverageInterestRate = createSelector(
  [selectContractsList],
  (list) => {
    const totalRemaining = list.reduce((s, c) => s + c.amountRemaining, 0);
    if (totalRemaining === 0) return 0;
    const weighted = list.reduce(
      (s, c) => s + c.amountRemaining * c.interestRate,
      0,
    );
    return weighted / totalRemaining;
  },
);

export const selectLiquidityNext90Days = createSelector(
  [selectAllInstallments],
  (list) =>
    list
      .filter((i) => i.status !== 'paid' && inWindow(i, 0, 90))
      .reduce((s, i) => s + i.amount, 0),
);

export type AttentionUrgency = 'overdue' | 'today' | 'soon' | 'upcoming';

export interface AttentionItem {
  installment: ContractInstallment;
  contract?: Contract;
  daysDiff: number;
  urgency: AttentionUrgency;
}

const urgencyFor = (diff: number): AttentionUrgency => {
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (diff <= 7) return 'soon';
  return 'upcoming';
};

const daysFromToday = (iso: string): number => {
  const due = new Date(iso);
  const now = new Date();
  return (
    Math.floor(Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate()) / DAY) -
    Math.floor(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / DAY)
  );
};

export const selectOverdueAttention = createSelector(
  [selectAllInstallments, selectContractsById],
  (installments, contractsById): AttentionItem[] =>
    installments
      .filter(
        (i) =>
          i.status !== 'paid' &&
          !isContractSettled(contractsById[i.contractId]) &&
          daysFromToday(i.dueDate) < 0,
      )
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
      .map((installment) => {
        const diff = daysFromToday(installment.dueDate);
        return {
          installment,
          contract: contractsById[installment.contractId],
          daysDiff: diff,
          urgency: urgencyFor(diff),
        };
      }),
);

export const selectNextInstallmentByContract = createSelector(
  [selectAllInstallments, selectContractsById],
  (installments, contractsById): AttentionItem[] => {
    const nextByContract: Record<ContractId, ContractInstallment> = {};
    installments
      .filter(
        (i) =>
          i.status !== 'paid' && !isContractSettled(contractsById[i.contractId]),
      )
      .forEach((i) => {
        const diff = daysFromToday(i.dueDate);
        if (diff < 0) return;
        const current = nextByContract[i.contractId];
        if (!current || new Date(i.dueDate).getTime() < new Date(current.dueDate).getTime()) {
          nextByContract[i.contractId] = i;
        }
      });
    return Object.values(nextByContract)
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
      .map((installment) => {
        const diff = daysFromToday(installment.dueDate);
        return {
          installment,
          contract: contractsById[installment.contractId],
          daysDiff: diff,
          urgency: urgencyFor(diff),
        };
      });
  },
);

export const selectAttentionAll = createSelector(
  [selectAllInstallments, selectContractsById],
  (installments, contractsById): AttentionItem[] =>
    installments
      .filter(
        (i) =>
          i.status !== 'paid' && !isContractSettled(contractsById[i.contractId]),
      )
      .filter((i) => {
        const diff = daysFromToday(i.dueDate);
        return diff <= 30;
      })
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
      .map((installment) => {
        const diff = daysFromToday(installment.dueDate);
        return {
          installment,
          contract: contractsById[installment.contractId],
          daysDiff: diff,
          urgency: urgencyFor(diff),
        };
      }),
);

export interface AlertHeroSummary {
  status: AlertStatus;
  focus?: AttentionItem;
  overdueCount: number;
  upcoming30Count: number;
  upcoming30Amount: number;
  totalPendingCount: number;
}

export const selectAlertHero = createSelector(
  [selectOverdueAttention, selectNextInstallmentByContract, selectAllInstallments],
  (overdues, nextPerContract, all): AlertHeroSummary => {
    const upcoming30 = all.filter((i) => {
      if (i.status === 'paid') return false;
      const diff = daysFromToday(i.dueDate);
      return diff >= 0 && diff <= 30;
    });
    const upcoming30Amount = upcoming30.reduce((s, i) => s + i.amount, 0);
    const totalPendingCount = all.filter((i) => i.status !== 'paid').length;

    let status: AlertStatus = 'success';
    if (overdues.length > 0) status = 'critical';
    else if (upcoming30.length > 0) status = 'warning';

    const focus = overdues[0] ?? nextPerContract[0];

    return {
      status,
      focus,
      overdueCount: overdues.length,
      upcoming30Count: upcoming30.length,
      upcoming30Amount,
      totalPendingCount,
    };
  },
);

export interface ContractProgressSummary {
  paidAmount: number;
  remainingAmount: number;
  totalAmount: number;
  percentPaid: number;
  installmentsPaid: number;
  installmentsTotal: number;
  nextInstallment?: ContractInstallment;
  overdueCount: number;
}

export const selectContractProgress = (contractId: ContractId) =>
  createSelector(
    [selectContractById(contractId), selectInstallmentsForContract(contractId)],
    (contract, installments): ContractProgressSummary | undefined => {
      if (!contract) return undefined;
      const sorted = [...installments].sort(
        (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
      );
      const next = sorted.find((i) => i.status !== 'paid');
      const overdueCount = sorted.filter(
        (i) => i.status !== 'paid' && daysFromToday(i.dueDate) < 0,
      ).length;
      const percentPaid =
        contract.amountTotal > 0
          ? (contract.amountPaid / contract.amountTotal) * 100
          : 0;
      return {
        paidAmount: contract.amountPaid,
        remainingAmount: contract.amountRemaining,
        totalAmount: contract.amountTotal,
        percentPaid,
        installmentsPaid: contract.installmentsPaid,
        installmentsTotal: contract.installmentsTotal,
        nextInstallment: next,
        overdueCount,
      };
    },
  );
